import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { map, take } from "rxjs/operators";

import {
  CUSTOMER_ACTIVITY_SERVICE_URL,
  CUSTOMER_BRANCH_SERVICE_URL,
  CUSTOMER_DASHBOARD_SERVICE_URL,
  CUSTOMER_INVOICE_SERVICE_URL,
  CUSTOMER_SMS_SERVICE_URL,
  CUSTOMER_SERVICE_REQUEST_SERVICE_URL,
  CUSTOMER_STOCK_PRODUCT_SERVICE_URL,
  CUSTOMER_SUB_USER_SERVICE_URL,
  CUSTOMER_TASK_DRAFT_SERVICE_URL,
  CUSTOMER_TASK_SERVICE_URL
} from "environments/config";
import { CustomerActivityLogModel } from "app/models/objects/audit/activity-log.model";
import { CustomerInvoiceModel } from "app/models/objects/invoice/invoice.model";
import { CustomerStockProductModel } from "app/models/objects/stock/stock-product.model";
import { CustomerServiceRequestModel } from "app/models/objects/service-request/service-request.model";
import { CustomerTaskModel } from "app/models/objects/task/task.model";
import { CustomerUserModel } from "app/models/objects/user/user.model";

export interface CustomerFilterResponse<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
}

export interface CustomerTaskDraftPayload {
  task_type: string;
  forwarder_cargo?: boolean;
  schedule_date?: string;
  branch_uid?: string;
  destination_uid?: string;
  vehicle?: string;
  comment?: string;
  contact?: string;
  doc_number?: string;
  doc_number_date?: string;
  a_number?: string;
  a_number_date?: string;
  c_number?: string;
  c_number_date?: string;
  gan_c_number?: string;
  gan_c_number_date?: string;
  documents?: string[];
  template_file?: string;
  products?: Array<{
    uid?: string;
    name?: string;
    barcode?: string;
    item_code?: string;
    company_name?: string;
    quantity: number;
    palette_quantity?: number;
    requested_unit?: string;
    client_vat?: string;
    a_number?: string;
    a_number_date?: string;
    c_number?: string;
    c_number_date?: string;
    advance?: boolean;
    declaration_ulc?: boolean;
    root?: boolean;
    line_comment?: string;
  }>;
}

export interface CustomerTaskDraftTemplateRow {
  company_name?: string;
  client_vat?: string;
  quantity: number;
  palette_quantity?: number;
  a_number?: string;
  a_number_date?: string;
  c_number?: string;
  c_number_date?: string;
  advance?: boolean;
  declaration_ulc?: boolean;
  root?: boolean;
  line_comment?: string;
}

export interface CustomerBranchModel {
  uid: string;
  name: string;
  receive_docks?: Array<{
    uid?: string;
    id?: string;
    identifier?: string;
  }>;
}

export interface CustomerDashboardSummary {
  account?: {
    consignation: boolean;
    deposit: boolean;
    balance: number;
  };
  totals: {
    tasks: number;
    active_tasks: number;
    completed_tasks: number;
    draft_tasks: number;
    stock_products: number;
    stock_quantity: number;
    locked_sscc: number;
    sscc: number;
    unpaid_invoices: number;
    unpaid_invoice_amount: number;
    invoices: number;
    service_requests: number;
    open_service_requests: number;
    unseen_service_requests: number;
    branches: number;
  };
  parent_client_stats: {
    week: number;
    month: number;
    overall: number;
  };
  recent_tasks: CustomerTaskModel[];
  recent_invoices: CustomerInvoiceModel[];
  recent_service_requests: CustomerServiceRequestModel[];
  task_type_stats: Array<{ type: string; count: number }>;
}

export interface CustomerTaskDraftConfirmResponse {
  draft: CustomerTaskModel;
  task: CustomerTaskModel;
}

export interface CustomerServiceRequestPayload {
  request_type: string;
  title?: string;
  message?: string;
  custom_service?: string;
  contact_person?: string;
  phone?: string;
  attachments?: string[];
  payload?: Record<string, any>;
}

export interface CustomerSmsSettings {
  enabled: boolean;
  phone: string;
  user?: CustomerUserModel | null;
}

export interface CustomerGeneralAgreementCharge {
  name: string;
  charge_type?: string;
  amount: number;
  currency: string;
  start_date?: string;
  end_date?: string;
  general_agreement?: boolean;
}

@Injectable()
export class CustomerApiService {
  constructor(private http: HttpClient) {}

  dashboardSummary() {
    return this.http.get<CustomerDashboardSummary>(`${CUSTOMER_DASHBOARD_SERVICE_URL}/summary`).pipe(take(1));
  }

  tasks(page = 0, perPage = 12, find: any = {}) {
    return this.tasksResult(page, perPage, find).pipe(
      map((response) => response.items || [])
    );
  }

  tasksResult(page = 0, perPage = 12, find: any = {}) {
    return this.http.post<{ items: CustomerTaskModel[] }>(`${CUSTOMER_TASK_SERVICE_URL}/filter`, {
      page,
      per_page: perPage,
      find
    }).pipe(
      take(1),
      map((response: any) => ({
        items: response.items || [],
        total: response.total || 0,
        page: response.page || 0,
        per_page: response.per_page || perPage
      }))
    );
  }

  task(uid: string) {
    return this.http.get<CustomerTaskModel>(`${CUSTOMER_TASK_SERVICE_URL}/${uid}`).pipe(take(1));
  }

  ssccHistoryResult(page = 0, perPage = 15, find: any = {}, countOnly = false) {
    return this.http.post<any>(`${CUSTOMER_TASK_SERVICE_URL}/sscc-history`, {
      page,
      per_page: perPage,
      find,
      count_only: countOnly
    }).pipe(
      take(1),
      map((response: any) => ({
        items: response.items || [],
        total: response.total || 0,
        page: response.page || 0,
        per_page: response.per_page || perPage
      }))
    );
  }

  taskDrafts(page = 0, perPage = 12, find: any = {}) {
    return this.taskDraftsResult(page, perPage, find).pipe(
      map((response) => response.items || [])
    );
  }

  taskDraftsResult(page = 0, perPage = 12, find: any = {}) {
    return this.http.post<{ items: CustomerTaskModel[] }>(`${CUSTOMER_TASK_DRAFT_SERVICE_URL}/filter`, {
      page,
      per_page: perPage,
      find
    }).pipe(
      take(1),
      map((response: any) => ({
        items: response.items || [],
        total: response.total || 0,
        page: response.page || 0,
        per_page: response.per_page || perPage
      }))
    );
  }

  taskDraft(uid: string) {
    return this.http.get<CustomerTaskModel>(`${CUSTOMER_TASK_DRAFT_SERVICE_URL}/${uid}`).pipe(take(1));
  }

  branchesResult(page = 0, perPage = 50, find: any = {}) {
    return this.http.post<{ items: CustomerBranchModel[] }>(`${CUSTOMER_BRANCH_SERVICE_URL}/filter`, {
      page,
      per_page: perPage,
      find
    }).pipe(
      take(1),
      map((response: any) => ({
        items: response.items || [],
        total: response.total || 0,
        page: response.page || 0,
        per_page: response.per_page || perPage
      }))
    );
  }

  stockProducts(page = 0, perPage = 12, find: any = { term: "" }) {
    return this.stockProductsResult(page, perPage, find).pipe(
      map((response) => response.items || [])
    );
  }

  stockProductsResult(page = 0, perPage = 12, find: any = { term: "" }) {
    return this.http.post<{ items: CustomerStockProductModel[] }>(`${CUSTOMER_STOCK_PRODUCT_SERVICE_URL}/filter`, {
      page,
      per_page: perPage,
      find
    }).pipe(
      take(1),
      map((response: any) => ({
        items: response.items || [],
        total: response.total || 0,
        page: response.page || 0,
        per_page: response.per_page || perPage
      }))
    );
  }

  invoices(page = 0, perPage = 12, find: any = {}) {
    return this.invoicesResult(page, perPage, find).pipe(
      map((response) => response.items || [])
    );
  }

  invoicesResult(page = 0, perPage = 12, find: any = {}) {
    return this.http.post<{ items: CustomerInvoiceModel[] }>(`${CUSTOMER_INVOICE_SERVICE_URL}/filter`, {
      page,
      per_page: perPage,
      find
    }).pipe(
      take(1),
      map((response: any) => ({
        items: response.items || [],
        total: response.total || 0,
        page: response.page || 0,
        per_page: response.per_page || perPage
      }))
    );
  }

  invoice(uid: string) {
    return this.http.get<CustomerInvoiceModel>(`${CUSTOMER_INVOICE_SERVICE_URL}/${uid}`).pipe(take(1));
  }

  generalAgreementCharges() {
    return this.http.get<{ items: CustomerGeneralAgreementCharge[] }>(`${CUSTOMER_DASHBOARD_SERVICE_URL}/general_agreement_charges`).pipe(
      take(1),
      map((response: any) => response.items || [])
    );
  }

  smsSettings() {
    return this.http.get<CustomerSmsSettings>(`${CUSTOMER_SMS_SERVICE_URL}/settings`).pipe(take(1));
  }

  updateSmsSettings(payload: CustomerSmsSettings) {
    return this.http.post<CustomerSmsSettings>(`${CUSTOMER_SMS_SERVICE_URL}/settings`, payload).pipe(take(1));
  }

  setStockProductLock(uid: string, locked: boolean) {
    return this.http.post<CustomerStockProductModel>(`${CUSTOMER_STOCK_PRODUCT_SERVICE_URL}/${uid}/manual-lock`, {
      locked
    }).pipe(take(1));
  }

  updateStockProductSmsTarget(uid: string, payload: { target_user_uid: string; enabled: boolean; phone?: string }) {
    return this.http.post<CustomerStockProductModel>(`${CUSTOMER_STOCK_PRODUCT_SERVICE_URL}/${uid}/sms_targets`, payload).pipe(take(1));
  }

  createTaskDraft(payload: CustomerTaskDraftPayload) {
    return this.http.post<CustomerTaskModel>(`${CUSTOMER_TASK_DRAFT_SERVICE_URL}/`, payload).pipe(take(1));
  }

  exportInvoices(find: any = {}) {
    return this.http.post(`${CUSTOMER_INVOICE_SERVICE_URL}/export`, { find }, { responseType: "blob" }).pipe(take(1));
  }

  exportTasks(find: any = {}) {
    return this.http.post(`${CUSTOMER_TASK_SERVICE_URL}/export`, { find }, { responseType: "blob" }).pipe(take(1));
  }

  exportStockProducts(find: any = {}) {
    return this.http.post(`${CUSTOMER_STOCK_PRODUCT_SERVICE_URL}/export`, { find }, { responseType: "blob" }).pipe(take(1));
  }

  exportServiceRequests(find: any = {}) {
    return this.http.post(`${CUSTOMER_SERVICE_REQUEST_SERVICE_URL}/export`, { find }, { responseType: "blob" }).pipe(take(1));
  }

  downloadTaskDraftTemplate(forwarderCargo: boolean) {
    return this.http.get(`${CUSTOMER_TASK_DRAFT_SERVICE_URL}/template`, {
      params: {
        task_type: "receive_products",
        forwarder_cargo: forwarderCargo ? "true" : "false"
      },
      responseType: "blob"
    }).pipe(take(1));
  }

  importTaskDraftTemplate(file: File, forwarderCargo: boolean) {
    const data = new FormData();
    data.append("file", file, file.name);
    data.append("task_type", "receive_products");
    data.append("forwarder_cargo", forwarderCargo ? "true" : "false");
    return this.http.post<{ items: CustomerTaskDraftTemplateRow[]; template_file?: { uid: string; name?: string; url?: string } }>(`${CUSTOMER_TASK_DRAFT_SERVICE_URL}/template/import`, data).pipe(take(1));
  }

  updateTaskSmsTarget(uid: string, payload: { target_user_uid: string; enabled: boolean; phone?: string }) {
    return this.http.post<CustomerTaskModel>(`${CUSTOMER_TASK_SERVICE_URL}/${uid}/sms_targets`, payload).pipe(take(1));
  }

  updateTaskDraftSmsTarget(uid: string, payload: { target_user_uid: string; enabled: boolean; phone?: string }) {
    return this.http.post<CustomerTaskModel>(`${CUSTOMER_TASK_DRAFT_SERVICE_URL}/${uid}/sms_targets`, payload).pipe(take(1));
  }

  confirmTaskDraft(uid: string) {
    return this.http.post<CustomerTaskDraftConfirmResponse>(`${CUSTOMER_TASK_DRAFT_SERVICE_URL}/${uid}/confirm`, {}).pipe(take(1));
  }

  cancelTaskDraft(uid: string) {
    return this.http.post<CustomerTaskModel>(`${CUSTOMER_TASK_DRAFT_SERVICE_URL}/${uid}/cancel`, {}).pipe(take(1));
  }

  rejectTaskDraft(uid: string) {
    return this.http.post<CustomerTaskModel>(`${CUSTOMER_TASK_DRAFT_SERVICE_URL}/${uid}/reject`, {}).pipe(take(1));
  }

  serviceRequestsResult(page = 0, perPage = 12, find: any = {}) {
    return this.http.post<{ items: CustomerServiceRequestModel[] }>(`${CUSTOMER_SERVICE_REQUEST_SERVICE_URL}/filter`, {
      page,
      per_page: perPage,
      find
    }).pipe(
      take(1),
      map((response: any) => ({
        items: response.items || [],
        total: response.total || 0,
        page: response.page || 0,
        per_page: response.per_page || perPage
      }))
    );
  }

  createServiceRequest(payload: CustomerServiceRequestPayload) {
    return this.http.post<CustomerServiceRequestModel>(`${CUSTOMER_SERVICE_REQUEST_SERVICE_URL}/`, payload).pipe(take(1));
  }

  markServiceRequestSeen(uid: string) {
    return this.http.post<CustomerServiceRequestModel>(`${CUSTOMER_SERVICE_REQUEST_SERVICE_URL}/${uid}/seen`, {}).pipe(take(1));
  }

  cancelServiceRequest(uid: string) {
    return this.http.post<CustomerServiceRequestModel>(`${CUSTOMER_SERVICE_REQUEST_SERVICE_URL}/${uid}/cancel`, {}).pipe(take(1));
  }

  subUsersResult(page = 0, perPage = 12, find: any = {}) {
    return this.http.post<{ items: CustomerUserModel[] }>(`${CUSTOMER_SUB_USER_SERVICE_URL}/filter`, {
      page,
      per_page: perPage,
      find
    }).pipe(
      take(1),
      map((response: any) => ({
        items: response.items || [],
        total: response.total || 0,
        page: response.page || 0,
        per_page: response.per_page || perPage
      }))
    );
  }

  createSubUser(payload: Partial<CustomerUserModel> & { password: string }) {
    return this.http.post<CustomerUserModel>(`${CUSTOMER_SUB_USER_SERVICE_URL}/`, payload).pipe(take(1));
  }

  subUser(uid: string) {
    return this.http.get<CustomerUserModel>(`${CUSTOMER_SUB_USER_SERVICE_URL}/${uid}`).pipe(take(1));
  }

  updateSubUser(uid: string, payload: Partial<CustomerUserModel>) {
    return this.http.post<CustomerUserModel>(`${CUSTOMER_SUB_USER_SERVICE_URL}/${uid}`, payload).pipe(take(1));
  }

  updateSubUserPassword(uid: string, password: string) {
    return this.http.post<CustomerUserModel>(`${CUSTOMER_SUB_USER_SERVICE_URL}/${uid}/password`, { password }).pipe(take(1));
  }

  deleteSubUser(uid: string) {
    return this.http.delete(`${CUSTOMER_SUB_USER_SERVICE_URL}/${uid}`).pipe(take(1));
  }

  activityResult(page = 0, perPage = 20, find: any = {}) {
    return this.http.post<{ items: CustomerActivityLogModel[] }>(`${CUSTOMER_ACTIVITY_SERVICE_URL}/filter`, {
      page,
      per_page: perPage,
      find
    }).pipe(
      take(1),
      map((response: any) => ({
        items: response.items || [],
        total: response.total || 0,
        page: response.page || 0,
        per_page: response.per_page || perPage
      }))
    );
  }
}
